import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, MessageCircle, Trash2, Check, Clock, Users } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Erro ao carregar contactos');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/leads/${leadId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads(leads.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      toast.success('Estado atualizado');
    } catch (error) {
      toast.error('Erro ao atualizar estado');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/leads/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contacto eliminado');
      setLeads(leads.filter(l => l.id !== deleteId));
    } catch (error) {
      toast.error('Erro ao eliminar contacto');
    } finally {
      setDeleteId(null);
    }
  };

  const openWhatsApp = (phone, name) => {
    const message = encodeURIComponent(`Olá ${name}, estamos a entrar em contacto relativamente ao seu interesse nas nossas viaturas.`);
    window.open(`https://wa.me/${phone.replace(/\D/g, '')}?text=${message}`, '_blank');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') {
      return (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-500/10 text-red-500 border border-red-500/30">
          <Clock size={12} className="inline mr-1" />
          Pendente
        </span>
      );
    }
    return (
      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
        <Check size={12} className="inline mr-1" />
        Contactado
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-medium text-white mb-2" data-testid="leads-page-title">Gestão de Contactos</h1>
          <p className="text-zinc-500">{leads.length} contacto(s) recebido(s)</p>
        </div>

        {/* Leads List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-zinc-400">A carregar contactos...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 bg-[#12141D] border border-zinc-800">
            <Users size={48} className="mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">Ainda não recebeu nenhum contacto</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {leads.map((lead) => (
              <div 
                key={lead.id}
                className="bg-[#12141D] border border-zinc-800 p-6 hover:border-zinc-600 transition-colors"
                data-testid={`lead-card-${lead.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-medium text-white">{lead.name}</h3>
                      {getStatusBadge(lead.status)}
                    </div>
                    
                    <div className="flex items-center gap-4 text-zinc-400 text-sm mb-3">
                      <span className="flex items-center gap-1">
                        <Phone size={14} />
                        {lead.phone}
                      </span>
                      <span>•</span>
                      <span>{formatDate(lead.created_at)}</span>
                    </div>

                    {lead.car_info && (
                      <p className="text-red-500 text-sm mb-3">
                        Interesse em: {lead.car_info}
                      </p>
                    )}

                    <p className="text-zinc-300 bg-[#090A0F] p-4 border-l-2 border-amber-500">
                      "{lead.message}"
                    </p>
                  </div>

                  <div className="flex flex-row md:flex-col gap-2">
                    <button
                      onClick={() => openWhatsApp(lead.phone, lead.name)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-black font-semibold hover:bg-[#1DA851] transition-colors"
                      data-testid={`whatsapp-lead-${lead.id}`}
                    >
                      <MessageCircle size={18} />
                      <span className="hidden sm:inline">WhatsApp</span>
                    </button>
                    
                    {lead.status === 'pending' ? (
                      <button
                        onClick={() => handleStatusChange(lead.id, 'contacted')}
                        className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-white hover:bg-zinc-800 transition-colors"
                        data-testid={`mark-contacted-${lead.id}`}
                      >
                        <Check size={18} />
                        <span className="hidden sm:inline">Contactado</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStatusChange(lead.id, 'pending')}
                        className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-white hover:bg-zinc-800 transition-colors"
                        data-testid={`mark-pending-${lead.id}`}
                      >
                        <Clock size={18} />
                        <span className="hidden sm:inline">Pendente</span>
                      </button>
                    )}

                    <button
                      onClick={() => setDeleteId(lead.id)}
                      className="flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-500/10 transition-colors"
                      data-testid={`delete-lead-${lead.id}`}
                    >
                      <Trash2 size={18} />
                      <span className="hidden sm:inline">Eliminar</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-[#12141D] border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Eliminar Contacto</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Tem a certeza que deseja eliminar este contacto? Esta ação não pode ser revertida.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 text-white hover:bg-red-600"
                data-testid="confirm-delete-lead-btn"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLeads;
